/**
 * Authentication Context Provider.
 */
import { createContext, useContext, useState, useEffect } from "react";
import { apiClient } from "../utils/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Keys that are written outside the auth context but logically scope a session
// (impersonation banner state). They MUST be cleared whenever the active token
// changes via login or logout, otherwise stale UI state survives across
// sessions and shows a fake "you are on tenant X" banner.
const SESSION_SCOPED_KEYS = [
  "super_admin_original_token",
  "switched_tenant_name",
];

const clearSessionScopedKeys = () => {
  SESSION_SCOPED_KEYS.forEach((k) => localStorage.removeItem(k));
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      if (token) {
        try {
          const response = await apiClient.get('/auth/me');
          setUser(response.data);
        } catch (e) {
          console.error("Auth init failed:", e);
          localStorage.removeItem("token");
          clearSessionScopedKeys();
          setToken(null);
        }
      } else {
        // No token on boot — make sure no stale impersonation banner state survives.
        clearSessionScopedKeys();
      }
      setLoading(false);
    };
    initAuth();
  }, [token]);

  const login = async (email, password) => {
    const response = await apiClient.post('/auth/login', { email, password });
    const { token: newToken, user: userData } = response.data;
    // Wipe any previous impersonation banner state before establishing the new
    // session. Otherwise a previous "switched to tenant X" state would persist
    // and show a misleading banner on the fresh login.
    clearSessionScopedKeys();
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    clearSessionScopedKeys();
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
